import IBaseWorldData from 'System/World/Data/IBaseWorldData';
import Game from 'Game';
import { IFieldData } from 'State/World/IFieldData';
import { getMaxWorlds } from 'Constants/World';
import { WinCriteriaType } from 'Constants/WinCriteria';
import { IWinCriteriaData } from 'State/Level/ILevelState';
import { ILimit, MathHelper } from './MathHelper';
import { report } from 'report';
import { Model } from './layout/types';
import { WORLD_LAYOUTS } from '../../build/temp/WorldLayouts';
import { FieldToWorldMap } from '../../build/temp/WorldFieldMap';
import { isUndefined } from 'lodash';

type WorldLevelData = {
    goldGoalLevels: number[];
    hardLevels: number[];
    plinkoLevels: number[];
};

declare const __WORLD_LEVEL_DATA__: WorldLevelData;

/**
 * Type alias to distinguish dynamic field-sector ID strings.
 * TODO: Replace field Id string instances with type alias.
 * TODO: Use type-guard when reading from data to assert valid FieldSectorId type.
 */
export type FieldSectorId = string;

export type FieldSector = {
    field: string;
    sector: number;
};
export namespace WorldDataHelper {
    let _maxWorld: number;

    export function getWorldLayout(worldNumber: number) {
        return WORLD_LAYOUTS[worldNumber - 1];
    }

    export function getWorldLevelData(): WorldLevelData {
        return __WORLD_LEVEL_DATA__;
    }

    export function queueWorldData() {
        for (let i = 1; i <= getMaxWorlds(); i += 1) {
            Game.resource.add(`worldData${i}`, `resource/data/world/world_${i}/world.json`);
        }
    }

    export function getWorldNumber(levelNumber: number): number {
        const start = 1;
        if (isUndefined(levelNumber) || levelNumber <= 0) {
            return start;
        }
        const maxWorldNumber = maxWorld();
        for (let i = start; i <= maxWorldNumber; i += 1) {
            const { min, max } = getWorldLevelLimits(i);
            if (levelNumber < min) return i - 1;
            if (levelNumber >= min && levelNumber <= max) return i;
        }
        return maxWorldNumber + 1;
    }

    export function getWorldData(worldNumber: number): IBaseWorldData {
        if (Game.resource.existsAndLoaded(`worldData${worldNumber}`)) {
            return Game.resource.get<IBaseWorldData>(`worldData${worldNumber}`);
        }
        return undefined;
    }

    export function createWorldLayout(worldNumber: number) {
        const layout = getWorldLayout(worldNumber);
        return new layout();
    }

    export function getCropModels(worldNumber: number, fieldIndex: number): Model[] {
        const layout = getWorldLayout(worldNumber);
        const raw = layout.raw as any;
        const cropsContainerA = raw.list.filter((m: Model) => m.className === 'cropsSectorA')[fieldIndex];
        const cropsContainerB = raw.list.filter((m: Model) => m.className === 'cropsSectorB')[fieldIndex];

        return raw.list.filter((m: Model) => m.parent === cropsContainerA.id || m.parent === cropsContainerB.id);
    }

    export function getTotalWorldCount(): number {
        let worldNumber: number = 1;
        while (true) {
            if (Game.resource.existsOrQueued(`worldData${worldNumber}`)) {
                worldNumber++;
            } else {
                return --worldNumber;
            }
        }
    }

    export function getWorldLevelLimits(worldNumber: number): ILimit {
        const worldData: IBaseWorldData = getWorldData(worldNumber);
        try {
            if (worldData !== undefined) return worldData.limits;

            throw new Error('World data not found.');
        } catch (e) {
            report(e.message, {
                once: true,
                sampleRate: 0.1,
                tags: {
                    worldNumber,
                },
                fingerprint: 'getWorldLevelLimits:DataNotFound',
            });
        }
    }

    export function canGetWorldLevelLimits(worldNumber: number): Boolean {
        return getWorldData(worldNumber) !== undefined;
    }

    export function getAllFieldsData(worldNumber?: number): IFieldData[] {
        if (worldNumber) {
            return getWorldFieldData(worldNumber);
        } else {
            const maxWorldNumber: number = maxWorld();
            let fieldData: IFieldData[] = [];
            for (let i = 1; i < maxWorldNumber + 1; i++) {
                const worldFieldData: IFieldData[] = getWorldFieldData(i);

                fieldData = worldFieldData ? [...fieldData, ...worldFieldData] : fieldData;
            }
            return fieldData;
        }
    }
    export function getWorldFieldData(worldNumber: number): IFieldData[] {
        const worldDataKey: string = `worldData${worldNumber}`;

        if (!Game.resource.existsAndLoaded(worldDataKey)) return undefined;

        const worldData = Game.resource.get<IBaseWorldData>(worldDataKey);

        return worldData.fields.slice().sort((a: IFieldData, b: IFieldData) => a.requiredLevel[0] - b.requiredLevel[0]);
    }

    export function getAllFieldData(world?: number): IFieldData[] {
        const maxWorldNumber = world ?? maxWorld();
        let fieldData: IFieldData[] = [];
        for (let i = world ?? 1; i < maxWorldNumber + 1; i++) {
            const worldFieldData: IFieldData[] = getWorldFieldData(i);

            fieldData = worldFieldData ? [...fieldData, ...worldFieldData] : fieldData;
        }
        return fieldData;
    }

    export function maxLevel(worldNumber = maxWorld()): number {
        return getWorldLevelLimits(worldNumber).max;
    }

    export function maxWorld(): number {
        if (_maxWorld === undefined) {
            let i: number = 1;
            while (i <= getMaxWorlds() && getWorldData(i) !== undefined) {
                i++;
            }
            _maxWorld = i - 1;
        }
        return _maxWorld;
    }

    export function getLevelGoal(levelNumber: number): IWinCriteriaData[] {
        return [
            {
                type: WorldDataHelper.getWorldLevelData().goldGoalLevels.includes(levelNumber)
                    ? WinCriteriaType.CLEAR_GOLD
                    : WinCriteriaType.CLEAR_ALL,
            },
        ];
    }

    export function isHardLevel(levelNumber: number): boolean {
        return WorldDataHelper.getWorldLevelData().hardLevels.includes(levelNumber);
    }

    export function isPlinkoLevel(levelNumber: number): boolean {
        return WorldDataHelper.getWorldLevelData().plinkoLevels.includes(levelNumber);
    }

    export function getFieldByLevelNumber(levelNumber: number, worldNumber?: number): IFieldData {
        worldNumber = worldNumber ?? getWorldNumber(levelNumber);
        const worldFields = getWorldFieldData(worldNumber);
        const field = worldFields.find(fieldData => {
            return MathHelper.isWithinRangeLimit(levelNumber, fieldData.levelRange);
        });
        return field;
    }

    /**
     * Get the associated field data given the field Id.
     * TODO: Highly un-optimized. Please refactor.
     * @param fieldSectorId
     */
    export function getFieldData(fieldSectorId: FieldSectorId): IFieldData {
        const fieldId = getFieldNameFromId(fieldSectorId);
        const worldNumber = FieldToWorldMap.get(fieldId);
        return WorldDataHelper.getAllFieldData(worldNumber).find(field => field.id === fieldId);
    }

    /**
     * Get the associated field data given the field Id.
     * TODO: Highly un-optimized. Please refactor.
     * @param fieldId
     */
    export function getFieldDataByCropId(cropId: FieldSectorId, worldNumber?: number): IFieldData {
        return WorldDataHelper.getAllFieldData(worldNumber).find(field => field.id === cropId);
    }

    export function getFieldNameFromId(fieldId: FieldSectorId): string {
        return getFieldFromId(fieldId).field;
    }

    export function getFieldSectorFromId(fieldId: FieldSectorId): number {
        return getFieldFromId(fieldId).sector;
    }

    export function getFieldFromId(fieldId: FieldSectorId): FieldSector {
        const ids = fieldId.split('_');
        return {
            field: ids[0],
            sector: Number(ids[1]),
        };
    }
}
